/**
 * InfluentEntryPage.tsx  —  Regulatory-aware influent sample entry
 *
 * Key additions over the old version:
 *  • Per-standard required parameter checklist (UK EA / AU EPA / EU UWWTD)
 *  • Required parameter badge on every field that must be captured
 *  • Typical influent concentration range displayed as a guidance bar
 *  • Anomaly warning when a value is implausibly high (possible typo)
 *  • Live monitoring completeness panel (right column)
 *  • Post-save modal showing completeness vs standard requirements
 *  • Fixed standard codes (removed legacy AUS_NEPM, added EU_UWWTD)
 *  • Standard auto-derived from active session
 */

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import {
  Droplets, Save, Play, Building2, FlaskConical,
  TestTube, Beaker, Calendar, CheckCircle2,
  AlertTriangle, RotateCcw, ChevronDown, Shield,
  ShieldCheck, Activity, FileText, Hash,
  Info, X, ArrowRight, ClipboardList, Database,
  TrendingUp, TrendingDown, Waves, Globe, Clock, MapPin,
  Lock, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/context/SessionContext';
import { useProposal } from '@/context/ProposalContext';
import { useCalculator } from '@/hooks/useCalculator';
import { trpc } from '@/providers/trpc';
import { saveLocalInfluentSample, markSampleDbConfirmed, getLocalSites, saveLocalSite, type UnifiedSample, type CustomerSite } from '@/lib/hybridStorage';
import { hasPhpBackend, apiListCustomerSites } from '@/lib/phpApi';
import { ALL_STANDARDS, type StandardCode, type WastewaterStandard } from '@/types/standards';
import { generateTrendReportPDF, buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import FlowRatePanel from '@/components/FlowRatePanel';
import TreatmentAssessmentModal from '@/components/TreatmentAssessmentModal';
import RegulatoryNotice from '@/components/RegulatoryNotice';
import PageHeader from '@/components/PageHeader';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InfluentForm {
  sampleName: string;
  customerId: string;
  siteId: string;
  standard: string;
  date: string;
  notes: string;
  samplingMethod: string;
  sampledBy: string;
  // Core
  ph: string; cod: string; bod: string; bod5: string; tss: string;
  nh3n: string; tp: string; tn: string; oilgrease: string; tds: string;
  // Physical
  conductivity: string; temperature: string; turbidity: string; flow: string;
  // Metals
  lead: string; chromium: string; cadmium: string; nickel: string;
  zinc: string; copper: string; iron: string; manganese: string; arsenic: string;
  mercury: string;
  // Other
  no3n: string; sulfide: string; chloride: string; hardness: string;
  alkalinity: string; mlss: string; srt: string; do: string;
  phenol: string; surfactants: string;
}

const EMPTY_FORM: InfluentForm = {
  sampleName: '', customerId: '', siteId: '', standard: 'UK_EA',
  date: new Date().toISOString().split('T')[0],
  notes: '', samplingMethod: 'grab', sampledBy: '',
  ph: '', cod: '', bod: '', bod5: '', tss: '', nh3n: '', tp: '', tn: '',
  oilgrease: '', tds: '',
  conductivity: '', temperature: '', turbidity: '', flow: '',
  lead: '', chromium: '', cadmium: '', nickel: '', zinc: '', copper: '',
  iron: '', manganese: '', arsenic: '', mercury: '',
  no3n: '', sulfide: '', chloride: '', hardness: '',
  alkalinity: '', mlss: '', srt: '', do: '',
  phenol: '', surfactants: '',
};

interface Customer { id: string; name: string; company: string; standard?: string; }

// ─── Required parameters per standard ────────────────────────────────────────
// UK EA: core parameters needed to assess trade effluent consent
// AU EPA: the 13 parameters mandated by ANZECC/ARMCANZ 2000 Schedule B1
// EU UWWTD: parameters required under Directive 91/271/EEC Article 4/5

const REQUIRED_PARAMS: Record<string, string[]> = {
  UK_EA: [
    'ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease', 'tds', 'conductivity', 'temperature',
    // EQS Directive metals & priority substances
    'lead', 'mercury', 'cadmium', 'chromium', 'arsenic', 'nickel', 'zinc', 'copper',
    // EA permit parameters
    'sulfide', 'phenol', 'surfactants', 'chloride',
  ],
  AU_EPA: [
    'ph', 'tss', 'bod5', 'cod', 'tn', 'tp', 'nh3n', 'oilgrease', 'conductivity', 'tds', 'temperature',
    // ANZG 2018 toxicant DGVs — all 8 key metals
    'lead', 'mercury', 'cadmium', 'chromium', 'arsenic', 'nickel', 'zinc', 'copper',
    'sulfide', 'phenol', 'surfactants',
  ],
  EU_UWWTD: [
    'ph', 'cod', 'bod5', 'tss', 'tp', 'tn', 'nh3n', 'oilgrease',
    'tds', 'conductivity', 'temperature',
    // EQS Directive 2013/39/EU metals
    'lead', 'mercury', 'cadmium', 'chromium', 'arsenic', 'nickel', 'zinc', 'copper',
    'sulfide', 'phenol', 'surfactants', 'chloride',
  ],
  US_EPA: [
    'ph', 'cod', 'bod5', 'tss', 'nh3n', 'tp', 'tn', 'oilgrease',
    'tds', 'conductivity', 'temperature',
    // EPA National Recommended Criteria — freshwater aquatic life
    'lead', 'mercury', 'cadmium', 'chromium', 'arsenic', 'nickel', 'zinc', 'copper',
    'sulfide', 'phenol', 'surfactants', 'chloride',
  ],
};

// Regulatory references for required param sets
const REQUIRED_REFS: Record<string, string> = {
  UK_EA:    'EA Trade Effluent Consent — EPR 2016 / Water Industry Act 1991 s118 / EQS Directive 2013/39/EU',
  AU_EPA:   'ANZG 2018 Toxicant DGVs / ANZECC/ARMCANZ 2000 Schedule B1 / State EPA licence conditions',
  EU_UWWTD: 'UWWTD 91/271/EEC Article 4/5 / EQS Directive 2013/39/EU / WFD 2000/60/EC',
  US_EPA:   'NPDES Permit — CWA 33 U.S.C. §1251 / 40 CFR Part 133 / EPA National Recommended Criteria',
};

// ─── Parameter metadata: label, unit, typical range, anomaly threshold ────────

interface ParamMeta {
  label: string;
  unit: string;
  group: 'core' | 'physical' | 'metal' | 'nutrient' | 'other';
  typicalMin: number;
  typicalMax: number;
  anomalyAbove?: number; // flag as possible error if > this
  tip?: string;
}

type ParamField = keyof Omit<
  InfluentForm,
  'sampleName'|'customerId'|'siteId'|'standard'|'date'|'notes'|'samplingMethod'|'sampledBy'|'flow'
>;


const PARAM_META: Record<ParamField, ParamMeta> = {
  ph:          { label: 'pH',            unit: '',         group: 'core',     typicalMin: 3.5,  typicalMax: 12.0, anomalyAbove: 14,    tip: 'Industrial influent pH range typically 3.5–12' },
  cod:         { label: 'COD',           unit: 'mg/L',     group: 'core',     typicalMin: 200,  typicalMax: 20000,anomalyAbove: 150000,tip: 'Chemical Oxygen Demand — raw industrial typically 500–15000' },
  bod:         { label: 'BOD',           unit: 'mg/L',     group: 'core',     typicalMin: 100,  typicalMax: 10000,anomalyAbove: 80000, tip: 'Biochemical Oxygen Demand (5-day equiv.)' },
  bod5:        { label: 'BOD₅',         unit: 'mg/L',     group: 'core',     typicalMin: 100,  typicalMax: 10000,anomalyAbove: 80000, tip: 'BOD₅ — preferred over BOD for standard compliance' },
  tss:         { label: 'TSS',           unit: 'mg/L',     group: 'core',     typicalMin: 100,  typicalMax: 5000, anomalyAbove: 60000, tip: 'Total Suspended Solids' },
  nh3n:        { label: 'NH₃-N',        unit: 'mg/L',     group: 'core',     typicalMin: 5,    typicalMax: 500,  anomalyAbove: 5000,  tip: 'Ammonia Nitrogen — high in food/beverage effluent' },
  tp:          { label: 'Total P',       unit: 'mg/L',     group: 'nutrient', typicalMin: 2,    typicalMax: 200,  anomalyAbove: 2000,  tip: 'Total Phosphorus — required by AU EPA' },
  tn:          { label: 'Total N',       unit: 'mg/L',     group: 'nutrient', typicalMin: 10,   typicalMax: 500,  anomalyAbove: 5000,  tip: 'Total Nitrogen — required by AU EPA and EU UWWTD' },
  oilgrease:   { label: 'Oil & Grease',  unit: 'mg/L',     group: 'core',     typicalMin: 20,   typicalMax: 3000, anomalyAbove: 30000, tip: 'Gravimetric O&G — high in dairy, food, kitchen waste' },
  tds:         { label: 'TDS',           unit: 'mg/L',     group: 'physical', typicalMin: 500,  typicalMax: 15000,anomalyAbove: 100000,tip: 'Total Dissolved Solids' },
  conductivity:{ label: 'Conductivity',  unit: 'µS/cm',    group: 'physical', typicalMin: 200,  typicalMax: 20000,anomalyAbove: 200000,tip: 'Electrical conductivity — indicates dissolved ions' },
  temperature: { label: 'Temperature',   unit: '°C',       group: 'physical', typicalMin: 10,   typicalMax: 80,   anomalyAbove: 100,   tip: 'Influent temperature — high in hot process industries' },
  turbidity:   { label: 'Turbidity',     unit: 'NTU',      group: 'physical', typicalMin: 50,   typicalMax: 5000, anomalyAbove: 100000,tip: 'Turbidity — high turbidity correlates with TSS' },
  lead:        { label: 'Lead (Pb)',     unit: 'mg/L',     group: 'metal',    typicalMin: 0.01, typicalMax: 10,   anomalyAbove: 500,   tip: 'Required by AU EPA — List I substance UK' },
  chromium:    { label: 'Chromium (Cr)', unit: 'mg/L',     group: 'metal',    typicalMin: 0.01, typicalMax: 10,   anomalyAbove: 500,   tip: 'Required by AU EPA — List II substance UK' },
  cadmium:     { label: 'Cadmium (Cd)', unit: 'mg/L',     group: 'metal',    typicalMin: 0.001,typicalMax: 1,    anomalyAbove: 50,    tip: 'Required by AU EPA — List I substance UK/EU EQS' },
  nickel:      { label: 'Nickel (Ni)',   unit: 'mg/L',     group: 'metal',    typicalMin: 0.01, typicalMax: 10,   anomalyAbove: 200,   tip: 'EQS metal — check for metal finishing industries' },
  zinc:        { label: 'Zinc (Zn)',     unit: 'mg/L',     group: 'metal',    typicalMin: 0.1,  typicalMax: 50,   anomalyAbove: 1000,  tip: 'Common in galvanising, metal plating wastewaters' },
  copper:      { label: 'Copper (Cu)',   unit: 'mg/L',     group: 'metal',    typicalMin: 0.05, typicalMax: 20,   anomalyAbove: 500,   tip: 'EQS metal — present in PCB, plating, brewery' },
  iron:        { label: 'Iron (Fe)',     unit: 'mg/L',     group: 'metal',    typicalMin: 0.5,  typicalMax: 100,  anomalyAbove: 5000,  tip: 'High in mining, steel, groundwater-impacted sites' },
  manganese:   { label: 'Manganese',     unit: 'mg/L',     group: 'metal',    typicalMin: 0.05, typicalMax: 20,   anomalyAbove: 500,   tip: 'Manganese — associated with mining, battery waste' },
  arsenic:     { label: 'Arsenic (As)',  unit: 'mg/L',     group: 'metal',    typicalMin: 0.001,typicalMax: 1,    anomalyAbove: 50,    tip: 'ANZG DGV metal — present in mining, pesticide sites' },
  mercury:     { label: 'Mercury (Hg)', unit: 'mg/L',     group: 'metal',    typicalMin: 0.0001,typicalMax: 0.05, anomalyAbove: 5,   tip: 'Most strictly regulated metal — EQS 0.005 (UK/EU), 0.001 (AU); present in chlor-alkali, mining, thermometer waste' },
  no3n:        { label: 'NO₃-N',        unit: 'mg/L',     group: 'other',    typicalMin: 1,    typicalMax: 100,  anomalyAbove: 2000,  tip: 'Nitrate nitrogen' },
  sulfide:     { label: 'Sulfides',      unit: 'mg/L',     group: 'other',    typicalMin: 0,    typicalMax: 50,   anomalyAbove: 500,   tip: 'H₂S/sulfide — odour and corrosion risk' },
  chloride:    { label: 'Chlorides',     unit: 'mg/L',     group: 'other',    typicalMin: 100,  typicalMax: 5000, anomalyAbove: 50000, tip: 'High chlorides in food processing, brine waste' },
  hardness:    { label: 'Hardness',      unit: 'mg/L',     group: 'other',    typicalMin: 50,   typicalMax: 2000, anomalyAbove: 20000, tip: 'CaCO₃ hardness — affects metal toxicity limits' },
  alkalinity:  { label: 'Alkalinity',    unit: 'mg/L',     group: 'other',    typicalMin: 50,   typicalMax: 2000, anomalyAbove: 20000, tip: 'Bicarbonate alkalinity — buffers pH swing' },
  mlss:        { label: 'MLSS',          unit: 'mg/L',     group: 'other',    typicalMin: 1000, typicalMax: 8000, anomalyAbove: 30000, tip: 'Mixed Liquor Suspended Solids — if biological treatment present' },
  srt:         { label: 'SRT',           unit: 'days',     group: 'other',    typicalMin: 3,    typicalMax: 30,   anomalyAbove: 100,   tip: 'Sludge Retention Time' },
  do:          { label: 'Dissolved O₂', unit: 'mg/L',     group: 'other',    typicalMin: 0,    typicalMax: 10,   anomalyAbove: 20,    tip: 'Dissolved oxygen — typically near zero in raw influent' },
  phenol:      { label: 'Phenol',        unit: 'mg/L',     group: 'other',    typicalMin: 0.1,  typicalMax: 500,  anomalyAbove: 5000,  tip: 'Phenolic compounds — present in petrochemical, coking, pharmaceutical waste' },
  surfactants: { label: 'Surfactants',   unit: 'mg/L',     group: 'other',    typicalMin: 1,    typicalMax: 200,  anomalyAbove: 2000,  tip: 'MBAS/CTAS surfactants — present in detergent, food processing, laundry waste' },
};

// ─── Range guidance ───────────────────────────────────────────────────────────

interface RangeStatus { inRange: boolean; anomaly: boolean; pct: number }

function checkRange(field: ParamField, value: string): RangeStatus | null {
  const v = parseFloat(value);
  if (isNaN(v) || value.trim() === '') return null;
  const meta = PARAM_META[field];
  const anomaly = meta.anomalyAbove !== undefined && v > meta.anomalyAbove;
  const inRange = v >= meta.typicalMin && v <= meta.typicalMax;
  // pct = position within typical range (clamped 0–100)
  const range = meta.typicalMax - meta.typicalMin;
  const pct = range > 0 ? Math.max(0, Math.min(100, ((v - meta.typicalMin) / range) * 100)) : 50;
  return { inRange, anomaly, pct };
}

// ─── Input step / max helpers ─────────────────────────────────────────────────

/** Return the correct <input> step for a given parameter field */
function getInputStep(field: string): string {
  if (field === 'ph')           return '0.01';
  if (['temperature', 'turbidity', 'do'].includes(field)) return '0.1';
  if (['flowrate', 'flow'].includes(field)) return '0.1';
  if (field === 'conductivity') return '1';
  if (field === 'tds')          return '1';
  if (['lead','mercury','cadmium','arsenic','nickel','chromium',
       'zinc','copper','iron','manganese','phenol','sulfide'].includes(field)) return '0.001';
  return '0.1';
}
function getInputMax(field: string): string | undefined {
  if (field === 'ph')          return '14';
  if (field === 'temperature') return '100';
  return undefined;
}

// ─── Parameter Input with guidance ───────────────────────────────────────────

function ParamInput({
  field, form, onUpdate, requiredByStd, std,
}: {
  field: ParamField;
  form: InfluentForm;
  onUpdate: (f: keyof InfluentForm, v: string) => void;
  requiredByStd: boolean;
  std: string;
}) {
  const meta = PARAM_META[field];
  const value = form[field as keyof InfluentForm] as string;
  const rs = checkRange(field, value);
  const hasValue = value.trim() !== '';

  const borderColor = !hasValue
    ? requiredByStd
      ? 'border-amber-600/40 focus:border-amber-500/60'
      : 'border-slate-800/50 focus:border-rb-brand/40'
    : rs?.anomaly
      ? 'border-red-500/60 focus:border-red-500/70'
      : rs?.inRange
        ? 'border-emerald-500/40 focus:border-emerald-500/50'
        : 'border-sky-500/30 focus:border-sky-500/40';

  return (
    <div className="space-y-1">
      <label className="text-[10px] font-bold uppercase tracking-wider flex items-center justify-between gap-1">
        <span className={`flex items-center gap-1 ${requiredByStd ? 'text-slate-300' : 'text-slate-500'}`}>
          {meta.label}
          {requiredByStd && (
            <span className="px-1 py-0.5 rounded text-[10px] font-black bg-amber-500/15 text-amber-400 border border-amber-500/25">
              REQ
            </span>
          )}
        </span>
        <span className="text-[10px] text-slate-400 font-normal normal-case">{meta.unit}</span>
      </label>

      <div className="relative">
        <input
          type="number"
          step={getInputStep(field)}
          min="0"
          max={getInputMax(field)}
          value={value}
          onChange={(e) => onUpdate(field as keyof InfluentForm, e.target.value)}
          placeholder="—"
          className={`w-full pl-3 py-3 sm:py-2 rounded-lg bg-slate-800/50 border text-base sm:text-sm text-slate-200 placeholder-slate-700 focus:outline-none transition-all font-mono text-right tabular-nums ${hasValue ? 'pr-14' : 'pr-10'} ${borderColor}`}
        />
        <span className="absolute right-6 top-1/2 -translate-y-1/2 text-[9px] text-slate-600 pointer-events-none select-none leading-none">
          {meta.unit}
        </span>
        {hasValue && rs?.anomaly && (
          <span title="Value may be unusually high — please verify">
            <AlertTriangle className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-red-400" />
          </span>
        )}
        {hasValue && !rs?.anomaly && requiredByStd && (
          <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-emerald-400" />
        )}
      </div>

      {/* Range guidance bar */}
      {hasValue && rs && !rs.anomaly && (
        <div>
          <div className="h-0.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${rs.inRange ? 'bg-emerald-500/60' : 'bg-sky-400/60'}`}
              style={{ width: `${rs.pct}%` }}
            />
          </div>
          <p className={`text-[10px] mt-0.5 ${rs.inRange ? 'text-emerald-500/70' : 'text-sky-400/70'}`}>
            {rs.inRange ? 'Within typical range' : 'Outside typical range — verify'}
          </p>
        </div>
      )}

      {hasValue && rs?.anomaly && (
        <p className="text-[10px] text-red-400/80">⚠ Unusually high — check units</p>
      )}

      {!hasValue && requiredByStd && (
        <p className="text-[10px] text-amber-600/70">Required for {std.replace('_', ' ')}</p>
      )}
    </div>
  );
}

// ─── Monitoring Completeness Panel ────────────────────────────────────────────

function MonitoringCompletenessPanel({
  form, stdCode,
}: {
  form: InfluentForm; stdCode: string;
}) {
  const required = REQUIRED_PARAMS[stdCode] || REQUIRED_PARAMS.UK_EA;
  const std = ALL_STANDARDS.find(s => s.code === stdCode);

  const paramFields = Object.keys(PARAM_META) as ParamField[];
  const entered = paramFields.filter(f => (form[f as keyof InfluentForm] as string).trim() !== '');

  const reqEntered   = required.filter(f => (form[f as keyof InfluentForm] as string || '').trim() !== '');
  const reqMissing   = required.filter(f => (form[f as keyof InfluentForm] as string || '').trim() === '');
  const anomalies    = entered.filter(f => checkRange(f as ParamField, form[f as keyof InfluentForm] as string)?.anomaly);

  const pct = required.length > 0 ? Math.round(reqEntered.length / required.length * 100) : 0;
  const allRequired  = reqMissing.length === 0;

  if (entered.length === 0) {
    return (
      <div className="p-4 text-center rb-card">
        <ClipboardList className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-[10px] text-slate-600">Enter parameter values to track monitoring completeness</p>
      </div>
    );
  }

  const bannerColor = allRequired
    ? 'border-emerald-500/30 bg-emerald-500/5'
    : reqEntered.length > 0
      ? 'border-amber-500/30 bg-amber-500/5'
      : 'border-slate-800/50 bg-slate-800/20';

  return (
    <div className="overflow-hidden rb-card">
      {/* Banner */}
      <div className={`px-4 py-3 border-b ${bannerColor} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {allRequired
            ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            : <ClipboardList className="w-4 h-4 text-amber-400 shrink-0" />
          }
          <div>
            <p className={`text-xs font-black ${allRequired ? 'text-emerald-400' : 'text-amber-400'}`}>
              {allRequired
                ? 'All required parameters entered'
                : `${reqMissing.length} required parameter${reqMissing.length > 1 ? 's' : ''} missing`
              }
            </p>
            <p className="text-[10px] text-slate-500">
              {reqEntered.length}/{required.length} required · {entered.length} total entered
            </p>
          </div>
        </div>
        <span className={`text-xs font-black ${allRequired ? 'text-emerald-400' : 'text-amber-400'}`}>{pct}%</span>
      </div>

      {/* Progress bar */}
      <div className="px-4 py-2 border-b border-slate-800/60">
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${allRequired ? 'bg-emerald-500' : pct > 50 ? 'bg-amber-500' : 'bg-sky-500'}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 divide-x divide-slate-800/60">
        {[
          { label: 'Required', value: required.length, color: 'text-amber-400' },
          { label: 'Entered',  value: reqEntered.length, color: 'text-emerald-400' },
          { label: 'Total',    value: entered.length, color: 'text-sky-400' },
        ].map(s => (
          <div key={s.label} className="text-center py-2">
            <p className={`text-base font-black ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-600 uppercase">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Missing required */}
      {reqMissing.length > 0 && (
        <div className="border-t border-slate-800/60 px-3 py-2">
          <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-1.5">
            Missing Required Parameters
          </p>
          <div className="flex flex-wrap gap-1">
            {reqMissing.map(f => (
              <span key={f} className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                {PARAM_META[f as ParamField]?.label || f.toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Anomaly warnings */}
      {anomalies.length > 0 && (
        <div className="border-t border-slate-800/60 px-3 py-2">
          <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-1.5">
            Values to Verify
          </p>
          {anomalies.map(f => (
            <div key={f} className="flex items-center justify-between px-2 py-1 rounded bg-red-500/5 border border-red-500/15 mb-1">
              <span className="text-[10px] text-slate-300">
                {PARAM_META[f as ParamField]?.label || f}
              </span>
              <span className="text-[10px] font-mono text-red-400">
                {form[f as keyof InfluentForm]} {PARAM_META[f as ParamField]?.unit}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Standard ref */}
      {std && (
        <div className="border-t border-slate-800/60 px-3 py-2">
          <p className="text-[10px] text-slate-600 italic">{std.flag} {REQUIRED_REFS[stdCode] || std.actReference}</p>
        </div>
      )}
    </div>
  );
}

// ─── Treatment Requirements Panel ────────────────────────────────────────────
// Compares each entered influent parameter against the selected standard's
// discharge limits and shows how much removal is required for compliance.

// Mapping from influent PARAM_META key → standard limits key
const INFLUENT_TO_LIMIT_KEY: Partial<Record<ParamField, keyof WastewaterStandard['limits']>> = {
  cod:         'cod',
  bod:         'bod5',
  bod5:        'bod5',
  tss:         'tss',
  nh3n:        'ammoniaN',
  tp:          'totalPhosphorus',
  tn:          'totalNitrogen',
  oilgrease:   'oilGrease',
  tds:         'tds',
  conductivity:'conductivity',
  temperature: 'temperature',
  lead:        'lead',
  mercury:     'mercury',
  cadmium:     'cadmium',
  chromium:    'chromium',
  arsenic:     'arsenic',
  nickel:      'nickel',
  zinc:        'zinc',
  copper:      'copper',
  sulfide:     'sulfides',
  phenol:      'phenol',
  surfactants: 'surfactants',
  chloride:    'chlorides',
};

interface RemovalRequirement {
  field: ParamField;
  label: string;
  unit: string;
  influent: number;
  limit: number;
  removalNeeded: number; // %
  alreadyMeets: boolean;
}

function TreatmentRequirementsPanel({
  form, std,
}: {
  form: InfluentForm;
  std: WastewaterStandard | null;
}) {
  if (!std) return null;

  const requirements: RemovalRequirement[] = [];

  (Object.keys(INFLUENT_TO_LIMIT_KEY) as ParamField[]).forEach(field => {
    const raw = (form[field as keyof InfluentForm] as string).trim();
    if (!raw) return;
    const influent = parseFloat(raw);
    if (isNaN(influent) || influent <= 0) return;
    const limitKey = INFLUENT_TO_LIMIT_KEY[field]!;
    const limit = std.limits[limitKey] as number;
    if (!limit || limit <= 0) return;

    const meta = PARAM_META[field];
    if (influent <= limit) {
      requirements.push({ field, label: meta.label, unit: meta.unit, influent, limit, removalNeeded: 0, alreadyMeets: true });
    } else {
      const removalNeeded = Math.round(((influent - limit) / influent) * 100);
      requirements.push({ field, label: meta.label, unit: meta.unit, influent, limit, removalNeeded, alreadyMeets: false });
    }
  });

  const needsRemoval = requirements.filter(r => !r.alreadyMeets).sort((a, b) => b.removalNeeded - a.removalNeeded);
  const alreadyOk    = requirements.filter(r => r.alreadyMeets);

  if (requirements.length === 0) return null;

  return (
    <div className="overflow-hidden mt-4 rb-card">
      <div className="px-4 py-3 border-b border-slate-800/60 flex items-center gap-2">
        <TrendingDown className="w-3.5 h-3.5 text-rb-brand" />
        <p className="text-[10px] font-bold text-rb-brand uppercase tracking-wider">
          Treatment Removal Required
        </p>
        <span className="ml-auto text-[10px] text-slate-600">{std.flag} {std.shortName}</span>
      </div>

      {needsRemoval.length === 0 ? (
        <div className="px-4 py-3">
          <p className="text-[10px] text-emerald-400 font-bold flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" />
            All entered values already meet discharge limits — no treatment required for these parameters.
          </p>
        </div>
      ) : (
        <div className="px-3 py-2 space-y-2">
          <p className="text-[10px] text-slate-500 mb-1">
            Minimum removal efficiency needed to meet {std.shortName} discharge limits:
          </p>
          {needsRemoval.map(r => (
            <div key={r.field} className="space-y-0.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-300">{r.label}</span>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="font-mono text-slate-400">{r.influent} → ≤{r.limit} {r.unit}</span>
                  <span className={`font-black px-1.5 py-0.5 rounded ${
                    r.removalNeeded >= 90 ? 'text-red-400 bg-red-500/10'
                    : r.removalNeeded >= 70 ? 'text-amber-400 bg-amber-500/10'
                    : 'text-sky-400 bg-sky-500/10'
                  }`}>
                    {r.removalNeeded}% removal
                  </span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    r.removalNeeded >= 90 ? 'bg-red-500'
                    : r.removalNeeded >= 70 ? 'bg-amber-500'
                    : 'bg-sky-500'
                  }`}
                  style={{ width: `${Math.min(r.removalNeeded, 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {alreadyOk.length > 0 && (
        <div className="border-t border-slate-800/60 px-3 py-2">
          <p className="text-[10px] text-slate-600 mb-1">Already meets limits:</p>
          <div className="flex flex-wrap gap-1">
            {alreadyOk.map(r => (
              <span key={r.field} className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                ✓ {r.label}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-slate-800/60 px-3 py-1.5">
        <p className="text-[10px] text-slate-600 italic">
          Treatment efficiency context only — actual permit limits determined by {std.shortName} site assessment
        </p>
      </div>
    </div>
  );
}

// ─── Post-save Result Modal ───────────────────────────────────────────────────

function SaveResultModal({
  sample, stdCode, customer, onClose, onRunAssessment,
}: {
  sample: UnifiedSample;
  stdCode: string;
  customer: Customer | null;
  onClose: () => void;
  onRunAssessment: () => void;
}) {
  const navigate = useNavigate();
  const std = ALL_STANDARDS.find(s => s.code === stdCode);
  const required = REQUIRED_PARAMS[stdCode] || REQUIRED_PARAMS.UK_EA;
  const params = sample.params;

  const entered = required.filter(f => params[f] !== undefined);
  const missing = required.filter(f => params[f] === undefined);
  const pct = required.length > 0 ? Math.round(entered.length / required.length * 100) : 100;
  const allPresent = missing.length === 0;

  const handleExportPDF = () => {
    generateTrendReportPDF({
      id: String(sample.id),
      reportNumber: sample.reportNumber || buildRegulatoryReportNumber('INF', sample.standard || 'UK_EA'),
      sampleName: sample.sampleName,
      customerName: customer?.name || 'Unknown',
      customerCompany: customer?.company || 'Unknown',
      standard: sample.standard || 'UK_EA',
      date: new Date(sample.createdAt),
      preparerName: 'Reynolds & Bauhm',
      preparerCompany: 'Reynolds & Bauhm Ltd',
      params,
    });
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-lg overflow-hidden rb-section"
      >
        {/* Header */}
        <div className={`px-5 py-4 border-b ${allPresent ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {allPresent
                ? <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                : <ClipboardList className="w-6 h-6 text-amber-400" />
              }
              <div>
                <h2 className={`text-sm font-black ${allPresent ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {allPresent ? 'Influent Sample Saved — Complete' : 'Influent Sample Saved — Monitoring Incomplete'}
                </h2>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">{sample.reportNumber}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/50">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 divide-x divide-slate-800/60 border-b border-slate-800/60">
          {[
            { label: 'Required', value: required.length, color: 'text-white' },
            { label: 'Entered', value: entered.length, color: 'text-emerald-400' },
            { label: 'Missing', value: missing.length, color: missing.length > 0 ? 'text-amber-400' : 'text-slate-500' },
          ].map(s => (
            <div key={s.label} className="text-center py-3">
              <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-600 uppercase">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Completeness bar */}
        <div className="px-5 py-3 border-b border-slate-800/60">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${allPresent ? 'bg-emerald-500' : pct > 50 ? 'bg-amber-500' : 'bg-sky-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={`text-xs font-black ${allPresent ? 'text-emerald-400' : 'text-amber-400'}`}>{pct}%</span>
          </div>
        </div>

        {/* Missing params */}
        {missing.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-800/60">
            <p className="text-[10px] font-bold text-amber-400 uppercase mb-2">
              Missing Required ({std?.shortName})
            </p>
            <div className="flex flex-wrap gap-1">
              {missing.map(f => (
                <span key={f} className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  {PARAM_META[f as ParamField]?.label || f.toUpperCase()}
                </span>
              ))}
            </div>
            {std && (
              <p className="text-[10px] text-slate-600 mt-2 italic">{REQUIRED_REFS[stdCode]}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="p-4 space-y-2">
          <button
            onClick={onRunAssessment}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all"
          >
            <Play className="w-3.5 h-3.5" /> Run Treatment Assessment
          </button>
          <button
            onClick={() => { navigate(`/daf-spec?loadSample=${sample.id}`); onClose(); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 transition-all"
          >
            <FlaskConical className="w-3.5 h-3.5" /> Design DAF for This Sample
          </button>
          <button
            onClick={() => { navigate('/effluent-entry'); onClose(); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all"
          >
            <Beaker className="w-3.5 h-3.5" /> Add Linked Effluent Sample
          </button>
          <button
            onClick={handleExportPDF}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-[10px] font-bold bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-slate-700 transition-all"
          >
            <FileText className="w-3 h-3" /> Export PDF Report
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => { navigate('/customers'); onClose(); }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-slate-700 transition-all"
            >
              <Building2 className="w-3 h-3" /> Customers
            </button>
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold bg-sky-500/5 text-sky-400 border border-sky-500/15 hover:bg-sky-500/10 transition-all"
            >
              <Droplets className="w-3 h-3" /> Add Another
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Section Header helper ────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, label, color, count, reqCount }: {
  icon: React.ElementType; label: string; color: string; count?: number; reqCount?: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-4 h-4 ${color}`} />
      <h3 className={`text-sm font-bold ${color}`}>{label}</h3>
      {count !== undefined && (
        <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 text-[10px] font-bold">{count} params</span>
      )}
      {reqCount !== undefined && reqCount > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[10px] font-bold border border-amber-500/20">
          {reqCount} req
        </span>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function InfluentEntryPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { activeStandard, currentCustomer, userProfile } = useSession();
  const userCountry = (userProfile?.country as string | undefined)
    ?? (currentCustomer?.country as string | undefined)
    ?? '';
  const isAuUser = userCountry === 'AU' || userCountry === 'NZ';
  const { activeProposal, updateActiveProposal, logActivity: logProposalActivity } = useProposal();
  const { setParams, runAssessment } = useCalculator();

  // Determine starting standard: AU/NZ users default to AU_EPA
  const defaultStandard = isAuUser
    ? 'AU_EPA'
    : activeStandard || 'UK_EA';

  const [form, setForm] = useState<InfluentForm>({
    ...EMPTY_FORM,
    standard: defaultStandard,
    customerId: currentCustomer?.id || '',
    sampleName: `Influent Sample — ${new Date().toLocaleDateString('en-GB')}`,
  });

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSites, setCustomerSites] = useState<CustomerSite[]>([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [savedSample, setSavedSample] = useState<UnifiedSample | null>(null);
  const [showMetals, setShowMetals] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showQuickSite, setShowQuickSite] = useState(false);
  const [quickSiteName, setQuickSiteName] = useState('');
  const [quickSiteAddress, setQuickSiteAddress] = useState('');

  const reportNumber = useMemo(() => {
    const stdCode = ((currentCustomer as any)?.standard || activeStandard || 'UK_EA') as StandardCode;
    return buildRegulatoryReportNumber('INF', stdCode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cs = JSON.parse(localStorage.getItem('rb_customers') || '[]');
    setCustomers(cs);
  }, []);

  useEffect(() => {
    if (!form.customerId) { setCustomerSites([]); return; }
    // Derive the numeric DB customer ID (avoid timestamp IDs)
    const numCustId = (() => {
      if (currentCustomer) {
        const sid = (currentCustomer as any).serverId;
        if (sid) return Number(sid);
        const n = Number(currentCustomer.id);
        return currentCustomer.id.length < 13 && n > 0 ? n : 0;
      }
      const n = Number(form.customerId);
      return form.customerId.length < 13 && n > 0 ? n : 0;
    })();

    const localSites = getLocalSites(form.customerId);
    setCustomerSites(localSites); // show immediately from cache
    setForm(prev => ({ ...prev, siteId: '' })); // reset site when customer changes

    // For authenticated users also fetch from DB (sites may only exist there)
    if (isAuthenticated && hasPhpBackend() && numCustId > 0) {
      apiListCustomerSites(numCustId)
        .then(apiSites => {
          if (Array.isArray(apiSites) && apiSites.length > 0) {
            const normSites: CustomerSite[] = apiSites.map((s: any) => ({
              ...s,
              id: String(s.id),
              customerId: String(s.customerId ?? s.customer_id ?? ''),
            }));
            const dbIds = new Set(normSites.map(s => s.id));
            const localOnly = localSites.filter(s => !dbIds.has(s.id));
            setCustomerSites([...normSites, ...localOnly]);
          }
        })
        .catch(() => { /* keep local sites on network error */ });
    }
  }, [form.customerId, isAuthenticated, currentCustomer]);

  // Lock standard to active session customer's standard when set
  useEffect(() => {
    if (currentCustomer && (currentCustomer as any).standard) {
      setForm(prev => ({ ...prev, standard: (currentCustomer as any).standard }));
    } else if (isAuUser) {
      setForm(prev => ({ ...prev, standard: 'AU_EPA' }));
    }
  }, [currentCustomer?.id, isAuUser]);

  // Auto-populate sampledBy from authenticated user
  useEffect(() => {
    if (user) {
      const name = (user as any).name || (user as any).username || (user as any).email || '';
      if (name) setForm(prev => prev.sampledBy ? prev : { ...prev, sampledBy: name });
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const std = useMemo(
    () => ALL_STANDARDS.find(s => s.code === form.standard) || null,
    [form.standard]
  );
  const required = useMemo(
    () => REQUIRED_PARAMS[form.standard] || REQUIRED_PARAMS.UK_EA,
    [form.standard]
  );
  const activeCustomer = useMemo(
    () => customers.find(c => String(c.id) === String(form.customerId)) || null,
    [customers, form.customerId]
  );

  const update = (field: keyof InfluentForm, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const buildParams = (): Record<string, number> => {
    const p: Record<string, number> = {};
    const fields = [...Object.keys(PARAM_META), 'flow'] as (keyof InfluentForm)[];
    fields.forEach(f => {
      const v = parseFloat(form[f] as string);
      if (!isNaN(v)) p[f as string] = v;
    });
    return p;
  };

  const saveMutation = trpc.influent.save.useMutation({
    onSuccess: () => toast.success('Synced to database'),
    onError: () => toast.info('Saved locally — database sync pending'),
  });

  const handleSave = async () => {
    if (!form.sampleName.trim()) { toast.error('Sample name is required'); return; }
    if (!form.siteId && customerSites.length > 0) {
      toast.error('Please select a site — samples must be linked to a discharge site for regulatory report generation');
      return;
    }
    const params = buildParams();
    if (Object.keys(params).length === 0) { toast.error('Enter at least one parameter value'); return; }

    setSaving(true);
    // Resolve the customer's DB integer ID — serverId takes priority over local timestamp id
    const resolvedDbCustId = currentCustomer
      ? ((currentCustomer as any).serverId
          ?? (currentCustomer.id.length < 13 && Number(currentCustomer.id) > 0 ? Number(currentCustomer.id) : null))
      : (form.customerId ? parseInt(form.customerId) : null);
    const cid = resolvedDbCustId;

    const saved = saveLocalInfluentSample({
      id: Date.now(),
      customerId: cid,
      siteId: form.siteId || null,
      userId: 0,
      sampleName: form.sampleName.trim(),
      reportNumber,
      params,
      standard: form.standard,
      notes: form.notes || null,
      sampleType: 'influent',
      linkedSampleId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    if (activeProposal?.id) {
      updateActiveProposal({
        sampleIds: [...(activeProposal.sampleIds || []), String(saved.id)],
      });
      logProposalActivity('sample_linked', 'influent-entry', undefined, activeProposal.id, { sampleId: saved.id });
    }

    toast.success(
      activeProposal
        ? `Saved & linked to ${activeProposal.proposalNumber}`
        : `Saved — ${reportNumber}`
    );

    if (isAuthenticated) {
      try {
        // Resolve numeric siteId — site IDs from API are integers; local ones may be timestamp strings
      const numSiteId = form.siteId
        ? (form.siteId.length < 13 && Number(form.siteId) > 0 ? Number(form.siteId) : undefined)
        : undefined;
      const dbResult = await saveMutation.mutateAsync({
          customerId: cid ?? undefined,
          siteId: numSiteId,
          sampleType: 'influent',
          sampleName: form.sampleName.trim(),
          params,
          standard: form.standard,
          notes: form.notes || '',
        });
        // Mark local record as confirmed in DB
        markSampleDbConfirmed(saved.id, (dbResult as any)?.id);
      } catch { /* toast handled above */ }
    }

    setSaving(false);
    setSavedSample(saved);
  };

  const handleRunAssessment = async () => {
    const params = buildParams();
    if (Object.keys(params).length === 0) { toast.error('Enter parameters first'); return; }
    setRunning(true);
    setParams(params);
    await runAssessment();
    setRunning(false);
    setShowAssessmentModal(true);
  };

  const handleReset = () => setForm({
    ...EMPTY_FORM,
    standard: activeStandard || defaultStandard,
    customerId: currentCustomer?.id || '',
    sampleName: `Influent Sample — ${new Date().toLocaleDateString('en-GB')}`,
  });

  // Count required params per section
  const reqInGroup = (fields: ParamField[]) =>
    fields.filter(f => required.includes(f)).length;

  const stdColor =
    form.standard === 'AU_EPA'    ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
    : form.standard === 'EU_UWWTD' ? 'text-violet-400 border-violet-500/30 bg-violet-500/10'
    : 'text-blue-400 border-blue-500/30 bg-blue-500/10';

  const coreFields:     ParamField[] = ['ph', 'cod', 'bod5', 'tss', 'nh3n', 'oilgrease'];
  const nutrientFields: ParamField[] = ['tp', 'tn', 'tds', 'bod'];
  const physicalFields: ParamField[] = ['temperature', 'conductivity', 'turbidity'];
  const metalFields:    ParamField[] = ['lead', 'chromium', 'cadmium', 'nickel', 'zinc', 'copper', 'iron', 'manganese', 'arsenic', 'mercury'];
  const advancedFields: ParamField[] = ['no3n', 'sulfide', 'chloride', 'hardness', 'alkalinity', 'mlss', 'srt', 'do', 'phenol', 'surfactants'];

  return (
    <div className="space-y-6">

      <PageHeader
        icon={Droplets}
        iconColor="text-sky-400"
        title="Influent Sample Entry"
        subtitle="Raw wastewater — regulatory monitoring completeness tracking"
        badge={
          <div className="flex items-center gap-2 flex-wrap">
            {std && (
              <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold border ${stdColor}`}>
                <ShieldCheck className="w-3 h-3" />
                {std.flag} {std.shortName}
              </span>
            )}
            <span className="hidden sm:inline-block px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-[11px] text-slate-500 font-mono">
              {reportNumber}
            </span>
            {activeProposal && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-violet-500/10 text-violet-400 border border-violet-500/20">
                <FileText className="w-3 h-3" />{activeProposal.proposalNumber}
              </span>
            )}
          </div>
        }
      />

      {/* ── Regulatory Origin Banner ── */}
      {!form.customerId ? (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl border border-amber-500/25 bg-amber-500/5">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-amber-400">No customer selected</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Select a customer to automatically apply their regulatory standard.
            </p>
          </div>
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all shrink-0"
          >
            Customers <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-sky-500/20 bg-sky-500/5">
          <span className="text-base">{std?.flag || '🌍'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-sky-400">Regulatory standard applied from customer profile location</p>
            <p className="text-[10px] text-slate-500">{std?.name} — {REQUIRED_REFS[form.standard] || std?.actReference}</p>
          </div>
          <span className="px-2.5 py-1 rounded-lg text-[10px] font-black bg-sky-500/10 text-sky-400 border border-sky-500/20">
            {required.length} req. params
          </span>
        </div>
      )}

      <RegulatoryNotice standard={form.standard} context="influent" compact />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_2fr_1fr] gap-6">

        {/* ══ LEFT: Sample Details ══ */}
        <div className="space-y-4">

          {/* Flow Rate Panel */}
          <FlowRatePanel value={form.flow} onChange={(v) => update('flow', v)} />

          {/* Sample Info */}
          <div className="p-4 space-y-3 rb-inner-card">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-800/60">
              <TestTube className="w-4 h-4 text-sky-400" />
              <h2 className="text-sm font-bold text-slate-200">Sample Details</h2>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sample Name *</label>
              <input
                value={form.sampleName}
                onChange={e => update('sampleName', e.target.value)}
                placeholder="e.g. Raw Influent — Dairy Site"
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500/40"
              />
            </div>

            {/* ── Customer — locked when active session is set ── */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Building2 className="w-2.5 h-2.5" /> Customer
              </label>
              {currentCustomer ? (
                /* Locked — active session customer cannot be changed here */
                <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-cyan-500/8 border border-cyan-500/30 min-w-0">
                  <Lock className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  <span className="text-sm font-bold text-cyan-200 flex-1 min-w-0 truncate">
                    {activeCustomer?.company || activeCustomer?.name || currentCustomer.company || currentCustomer.name}
                  </span>
                  {activeCustomer?.standard && (
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 shrink-0 hidden xs:inline">
                      {activeCustomer.standard.replace('_', ' ')}
                    </span>
                  )}
                  <span className="text-[10px] text-cyan-600 shrink-0 hidden sm:inline">locked</span>
                </div>
              ) : (
                /* Unlocked — no active session, free to choose */
                <select
                  value={form.customerId}
                  onChange={e => {
                    const cid = e.target.value;
                    update('customerId', cid);
                    if (cid) {
                      const cust = customers.find(c => String(c.id) === cid);
                      if (cust?.standard && ['UK_EA', 'AU_EPA', 'EU_UWWTD'].includes(cust.standard)) {
                        update('standard', cust.standard);
                        toast.info(`Standard auto-set to ${cust.standard.replace(/_/g, ' ')} from customer profile location`);
                      }
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
                >
                  <option value="">— Select customer —</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.company || c.name}</option>
                  ))}
                </select>
              )}
              {/* Standard-origin indicator */}
              {form.customerId && activeCustomer?.standard && !currentCustomer && (
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-sky-500/5 border border-sky-500/15 text-[10px] text-sky-400">
                  <Globe className="w-2.5 h-2.5 shrink-0" />
                  Standard auto-applied from customer profile location
                </div>
              )}
              {form.customerId && !activeCustomer?.standard && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-amber-500/5 border border-amber-500/25 text-[10px] text-amber-400">
                  <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
                  Customer has no standard set — using default
                </div>
              )}
            </div>

            {/* ── Site Location — always selectable, regional laws apply from site ── */}
            {form.customerId && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <MapPin className="w-2.5 h-2.5" /> Site Location
                  </label>
                  {customerSites.length === 0 && (
                    <button
                      type="button"
                      onClick={() => setShowQuickSite(v => !v)}
                      className="flex items-center gap-1 text-[10px] font-bold text-teal-400 hover:text-teal-300 transition-colors"
                    >
                      <Plus className="w-2.5 h-2.5" />
                      {showQuickSite ? 'Cancel' : 'Add site'}
                    </button>
                  )}
                </div>

                {customerSites.length > 0 ? (
                  <>
                    <select
                      value={form.siteId}
                      onChange={e => {
                        const siteId = e.target.value;
                        update('siteId', siteId);
                        if (siteId) {
                          const site = customerSites.find(s => s.id === siteId);
                          // Auto-apply regional regulatory standard from site country/standard
                          const COUNTRY_STD: Record<string, string> = {
                            AU: 'AU_EPA', NZ: 'AU_EPA', GB: 'UK_EA', IE: 'UK_EA',
                            DE: 'EU_UWWTD', FR: 'EU_UWWTD', NL: 'EU_UWWTD', BE: 'EU_UWWTD',
                            IT: 'EU_UWWTD', ES: 'EU_UWWTD', PT: 'EU_UWWTD', PL: 'EU_UWWTD',
                            US: 'US_EPA', CA: 'US_EPA',
                          };
                          if (site?.standard && ['UK_EA', 'AU_EPA', 'EU_UWWTD', 'US_EPA'].includes(site.standard)) {
                            update('standard', site.standard);
                            toast.info(`Regulatory standard set to ${site.standard.replace(/_/g, ' ')} from site`);
                          } else if (site?.country && COUNTRY_STD[site.country]) {
                            update('standard', COUNTRY_STD[site.country]);
                            toast.info(`${COUNTRY_STD[site.country].replace(/_/g, ' ')} applied — site country ${site.country}`);
                          }
                        }
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none focus:border-teal-500/40"
                    >
                      <option value="">— Select site —</option>
                      {customerSites.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.state ? ` (${s.state})` : s.region ? ` (${s.region})` : ''}
                        </option>
                      ))}
                    </select>
                    {form.siteId && (() => {
                      const site = customerSites.find(s => s.id === form.siteId);
                      if (!site) return null;
                      return (
                        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-teal-500/5 border border-teal-500/20 text-[10px]">
                          <MapPin className="w-2.5 h-2.5 text-teal-400 shrink-0" />
                          <span className="text-teal-400 font-bold truncate">{site.address}{site.postcode ? `, ${site.postcode}` : ''}</span>
                          {site.permitRef && <span className="ml-auto text-slate-500 shrink-0 font-mono">{site.permitRef}</span>}
                        </div>
                      );
                    })()}
                  </>
                ) : !showQuickSite ? (
                  <p className="text-[10px] text-slate-600 px-2 py-1.5 rounded-lg border border-dashed border-slate-800/50 italic">
                    No sites registered for this customer — add one above
                  </p>
                ) : null}

                {/* Quick-add site form */}
                {showQuickSite && (
                  <div className="space-y-2 p-3 rounded-xl bg-teal-500/5 border border-teal-500/25">
                    <p className="text-[10px] font-bold text-teal-400 uppercase tracking-wider">New Site</p>
                    <input
                      value={quickSiteName}
                      onChange={e => setQuickSiteName(e.target.value)}
                      placeholder="Site name (e.g. Main Plant, Site B)"
                      className="w-full px-2.5 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40 rb-card-sm"
                    />
                    <input
                      value={quickSiteAddress}
                      onChange={e => setQuickSiteAddress(e.target.value)}
                      placeholder="Address / postcode"
                      className="w-full px-2.5 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-teal-500/40 rb-card-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!quickSiteName.trim()) { toast.error('Site name required'); return; }
                        const newSite = saveLocalSite({
                          customerId: form.customerId,
                          name: quickSiteName.trim(),
                          address: quickSiteAddress.trim() || '',
                          postcode: '',
                          country: activeCustomer?.country || userCountry || 'GB',
                          standard: activeCustomer?.standard || form.standard,
                          notes: '',
                        });
                        setCustomerSites(getLocalSites(form.customerId));
                        update('siteId', newSite.id);
                        setQuickSiteName(''); setQuickSiteAddress(''); setShowQuickSite(false);
                        toast.success(`Site "${newSite.name}" added`);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/15 border border-teal-500/30 text-[10px] font-bold text-teal-300 hover:bg-teal-500/25 transition-all active:scale-95"
                    >
                      <Plus className="w-3 h-3" /> Save Site
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <ShieldCheck className="w-2.5 h-2.5" /> Regulatory Standard
              </label>
              <select
                value={form.standard}
                onChange={e => update('standard', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
                disabled={!currentCustomer || !!(currentCustomer && (currentCustomer as any).standard)}
              >
                {isAuUser ? (
                  <>
                    <option value="AU_EPA">🇦🇺 Australian EPA (General)</option>
                    <option value="SA_WATER">🇦🇺 SA Water — Trade Waste (SA only)</option>
                  </>
                ) : userCountry === 'DE' || userCountry === 'FR' ? (
                  <>
                    <option value="EU_UWWTD">🇪🇺 EU Urban Wastewater</option>
                    <option value="UK_EA">🇬🇧 UK Environment Agency</option>
                  </>
                ) : (
                  <>
                    <option value="UK_EA">🇬🇧 UK Environment Agency</option>
                    <option value="AU_EPA">🇦🇺 Australian EPA</option>
                    <option value="SA_WATER">🇦🇺 SA Water — Trade Waste</option>
                    <option value="EU_UWWTD">🇪🇺 EU Urban Wastewater</option>
                    <option value="US_EPA">🇺🇸 US EPA (NPDES)</option>
                  </>
                )}
              </select>
              {isAuUser && form.standard === 'SA_WATER' && (
                <p className="text-xs text-blue-400/80 flex items-center gap-1">
                  <Info className="w-2.5 h-2.5" /> SA Water trade waste standard — excess charges apply to effluent samples
                </p>
              )}
              {!currentCustomer && !isAuUser && (
                <p className="text-xs text-amber-400/80 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Set an active customer session to select a regulatory standard
                </p>
              )}
              {currentCustomer && (currentCustomer as any).standard && !isAuUser && (
                <p className="text-xs text-sky-400/80 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Standard locked to customer profile: {((currentCustomer as any).standard as string).replace('_', ' ')}
                </p>
              )}
              {std && (
                <div className={`px-2.5 py-1.5 rounded-lg border text-[10px] ${stdColor}`}>
                  <p className="font-bold">{std.name}</p>
                  <p className="opacity-70">{std.permitType}</p>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Calendar className="w-2.5 h-2.5" /> Sample Date
              </label>
              <input
                type="date"
                value={form.date}
                onChange={e => update('date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sampling Method</label>
              <select
                value={form.samplingMethod}
                onChange={e => update('samplingMethod', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 focus:outline-none"
              >
                <option value="grab">Grab sample</option>
                <option value="composite">Time-composite</option>
                <option value="flow_proportional">Flow-proportional composite</option>
                <option value="24h_composite">24-hour composite</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sampled By</label>
              <input
                value={form.sampledBy}
                onChange={e => update('sampledBy', e.target.value)}
                placeholder="Name / lab ref"
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500/40"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Notes</label>
              <textarea
                value={form.notes}
                onChange={e => update('notes', e.target.value)}
                rows={2}
                placeholder="Source stream, process type, industry notes…"
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 space-y-2 rb-inner-card">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-black bg-gradient-to-r from-sky-500 to-sky-600 text-white hover:from-sky-600 hover:to-sky-700 transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Influent Sample'}
            </button>
            <button
              onClick={handleRunAssessment}
              disabled={running}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5" />
              {running ? 'Running…' : 'Run Treatment Assessment'}
            </button>
            <button
              onClick={handleReset}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-500 border border-slate-700 hover:bg-slate-700 transition-all"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Clear Form
            </button>
          </div>

          {/* Required params list */}
          <div className="p-3 rb-card">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-3.5 h-3.5 text-amber-400" />
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                {required.length} Required — {std?.shortName}
              </p>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {required.map(f => {
                const meta = PARAM_META[f as ParamField];
                const filled = (form[f as keyof InfluentForm] as string || '').trim() !== '';
                return (
                  <span
                    key={f}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold border transition-all ${
                      filled
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    }`}
                  >
                    {filled ? '✓' : '○'} {meta?.label || f}
                  </span>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-600 italic">{REQUIRED_REFS[form.standard]}</p>
          </div>
        </div>

        {/* ══ CENTRE: Parameter Entry ══ */}
        <div className="space-y-5">

          {/* ── Core Parameters ── */}
          <div className="p-5 rb-inner-card">
            <SectionHeader
              icon={FlaskConical} label="Core Parameters" color="text-sky-400"
              count={coreFields.length} reqCount={reqInGroup(coreFields)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {coreFields.map(f => (
                <ParamInput key={f} field={f} form={form} onUpdate={update}
                  requiredByStd={required.includes(f)} std={form.standard} />
              ))}
            </div>
          </div>

          {/* ── Nutrients ── */}
          <div className="p-5 rb-inner-card">
            <SectionHeader
              icon={TrendingUp} label="Nutrients & Organics" color="text-teal-400"
              count={nutrientFields.length} reqCount={reqInGroup(nutrientFields)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {nutrientFields.map(f => (
                <ParamInput key={f} field={f} form={form} onUpdate={update}
                  requiredByStd={required.includes(f)} std={form.standard} />
              ))}
            </div>
          </div>

          {/* ── Physical ── */}
          <div className="p-5 rb-inner-card">
            <SectionHeader
              icon={Globe} label="Physical Parameters" color="text-cyan-400"
              count={physicalFields.length} reqCount={reqInGroup(physicalFields)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {physicalFields.map(f => (
                <ParamInput key={f} field={f} form={form} onUpdate={update}
                  requiredByStd={required.includes(f)} std={form.standard} />
              ))}
            </div>
            {/* Note about flow */}
            <div className="mt-3 flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
              <Waves className="w-3 h-3 text-cyan-400 shrink-0 mt-0.5" />
              <p className="text-[10px] text-cyan-400/70">Flow rate is entered in the panel on the left — required for treatment sizing calculations</p>
            </div>
          </div>

          {/* ── Heavy Metals (collapsible) ── */}
          <div className="overflow-hidden rb-inner-card">
            <button
              onClick={() => setShowMetals(v => !v)}
              className="w-full flex items-center justify-between p-5 hover:bg-slate-800/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <TestTube className="w-4 h-4 text-red-400" />
                <span className="text-sm font-bold text-red-400">Heavy Metals</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 text-[10px] font-bold">10 params</span>
                {reqInGroup(metalFields) > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[10px] font-bold border border-amber-500/20">
                    {reqInGroup(metalFields)} req
                  </span>
                )}
                <span className="text-[10px] text-slate-500">
                  — EQS / ANZG metals monitoring
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showMetals ? 'rotate-180' : ''}`} />
            </button>
            {showMetals && (
              <div className="px-5 pb-5">
                {form.standard === 'AU_EPA' && (
                  <div className="mb-3 flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                    <Info className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-400/80">
                      AU EPA requires Lead, Chromium, Cadmium and Mercury — Schedule B1 ambient monitoring (ANZECC/ARMCANZ 2000). Mercury ANZG DGV: ≤ 0.001 mg/L (most strict).
                    </p>
                  </div>
                )}
                {(form.standard === 'UK_EA' || form.standard === 'EU_UWWTD') && (
                  <div className="mb-3 flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-sky-500/5 border border-sky-500/15">
                    <Info className="w-3 h-3 text-sky-400 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-sky-400/70">
                      List I & II dangerous substances (Directive 76/464/EEC) — mandatory screening for metal finishing, mining, and chemical industries
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {metalFields.map(f => (
                    <ParamInput key={f} field={f} form={form} onUpdate={update}
                      requiredByStd={required.includes(f)} std={form.standard} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Advanced (collapsible) ── */}
          <div className="overflow-hidden rb-inner-card">
            <button
              onClick={() => setShowAdvanced(v => !v)}
              className="w-full flex items-center justify-between p-5 hover:bg-slate-800/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-bold text-purple-400">Advanced Parameters</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 text-[10px] font-bold">10 params</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>
            {showAdvanced && (
              <div className="px-5 pb-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {advancedFields.map(f => (
                    <ParamInput key={f} field={f} form={form} onUpdate={update}
                      requiredByStd={required.includes(f)} std={form.standard} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-[10px] text-slate-600 px-1 flex-wrap">
            <span className="flex items-center gap-1">
              <span className="px-1 py-0.5 rounded text-[10px] font-black bg-amber-500/15 text-amber-400 border border-amber-500/25">REQ</span>
              = required by standard
            </span>
            <span className="flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />= entered</span>
            <span className="flex items-center gap-1"><span className="w-2 h-1 bg-emerald-500/60 rounded" />= typical range</span>
            <span className="flex items-center gap-1"><span className="w-2 h-1 bg-sky-400/60 rounded" />= outside typical</span>
            <span className="flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5 text-red-400" />= verify value</span>
          </div>
        </div>

        {/* ══ RIGHT: Live Completeness Panel ══ */}
        <div className="space-y-4">
          <div className="sticky top-4">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <ClipboardList className="w-3 h-3" /> Monitoring Completeness
            </p>
            <MonitoringCompletenessPanel form={form} stdCode={form.standard} />

            <TreatmentRequirementsPanel form={form} std={std} />

            {/* Quick tip */}
            <div className="mt-4 p-3 rb-card">
              <div className="flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-slate-400">Influent vs Effluent</p>
                  <p className="text-[10px] text-slate-600 leading-relaxed">
                    Influent data is used for treatment sizing and permit assessment. After treatment, add a linked effluent sample to verify discharge compliance.
                  </p>
                  <button
                    onClick={() => navigate('/effluent-entry')}
                    className="flex items-center gap-1 text-[10px] text-rb-brand hover:underline mt-1"
                  >
                    Add effluent sample <ArrowRight className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Compliance notes from standard */}
            {std && (
              <div className="mt-4 p-3 space-y-2 rb-card">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  {std.flag} {std.shortName} — Monitoring Requirements
                </p>
                <div className="space-y-1.5">
                  {std.complianceNotes.slice(0, 4).map((note, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <div className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${
                        form.standard === 'AU_EPA'    ? 'bg-emerald-400'
                        : form.standard === 'EU_UWWTD' ? 'bg-violet-400'
                        : 'bg-blue-400'
                      }`} />
                      <p className="text-[10px] text-slate-500 leading-relaxed">{note}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600 italic">{std.actReference}</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Post-save Modal ── */}
      <AnimatePresence>
        {savedSample && (
          <SaveResultModal
            sample={savedSample}
            stdCode={form.standard}
            customer={activeCustomer}
            onClose={() => setSavedSample(null)}
            onRunAssessment={() => { setSavedSample(null); handleRunAssessment(); }}
          />
        )}
      </AnimatePresence>

      {/* ── Treatment Assessment Modal ── */}
      <TreatmentAssessmentModal
        open={showAssessmentModal}
        onClose={() => setShowAssessmentModal(false)}
        sampleName={form.sampleName || 'Influent Sample'}
      />
    </div>
  );
}
